import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  loadSquareSdk,
  type SquareCard,
  type SquarePayments,
} from '../lib/squareSdk';
import type { CheckoutConfig } from '../lib/checkout';

export interface TokenizeChargeDetails {
  amount: string;
  currencyCode?: string;
}

export interface SquarePaymentFormHandle {
  tokenize: (details: TokenizeChargeDetails) => Promise<string>;
  getPostalCode: () => string;
}

interface SquarePaymentFormProps {
  config: CheckoutConfig;
  disabled?: boolean;
  onReadyChange?: (ready: boolean) => void;
}

const POSTAL_CODE_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const SquarePaymentForm = forwardRef<SquarePaymentFormHandle, SquarePaymentFormProps>(
  function SquarePaymentForm({ config, disabled = false, onReadyChange }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<SquareCard | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [postalCode, setPostalCode] = useState('');

    useImperativeHandle(ref, () => ({
      async tokenize({ amount, currencyCode = 'CAD' }) {
        const card = cardRef.current;
        if (!card) {
          throw new Error('Payment form is not ready');
        }

        const trimmedPostalCode = postalCode.trim().toUpperCase();
        // Square's Card element decides on its own whether to render/require an internal
        // postal-code field based on the card's issuing country (no options.postalCode at
        // construction time — passing a fixed placeholder there previously left Square's own
        // field permanently invalid and blocked tokenization for any card requiring it). The
        // buyer's postal code — collected via our own separate field above — belongs in
        // verificationDetails.billingContact at tokenize() time instead, per Square's
        // ChargeCardVerificationDetails: https://developer.squareup.com/reference/sdks/web/payments/objects/ChargeCardVerificationDetails
        const result = await card.tokenize({
          amount,
          currencyCode,
          intent: 'CHARGE',
          customerInitiated: true,
          sellerKeyedIn: false,
          ...(trimmedPostalCode ? { billingContact: { postalCode: trimmedPostalCode } } : {}),
        });
        if (result.status !== 'OK' || !result.token) {
          const message =
            result.errors?.map((entry) => entry.message).filter(Boolean).join(' ') ||
            'Card details are invalid';
          throw new Error(message);
        }
        return result.token;
      },
      getPostalCode() {
        return postalCode.trim().toUpperCase();
      },
    }));

    useEffect(() => {
      if (!config.enabled || !config.applicationId || !config.locationId || !config.sdkUrl) {
        setLoading(false);
        onReadyChange?.(false);
        return;
      }

      let cancelled = false;

      async function init() {
        try {
          await loadSquareSdk(config.sdkUrl!);
          if (cancelled || !containerRef.current || !window.Square) return;

          const payments: SquarePayments = await window.Square.payments(
            config.applicationId!,
            config.locationId!
          );
          const card = await payments.card();
          await card.attach('#square-card-container');

          cardRef.current = card;
          setError(null);
          onReadyChange?.(true);
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Could not load payment form');
            onReadyChange?.(false);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }

      init();

      return () => {
        cancelled = true;
        onReadyChange?.(false);
        cardRef.current?.destroy().catch(() => undefined);
        cardRef.current = null;
      };
    }, [
      config.applicationId,
      config.enabled,
      config.locationId,
      config.sdkUrl,
      onReadyChange,
    ]);

    if (!config.enabled) {
      return (
        <div className="status-banner error">
          Square checkout is not configured on the server. Add sandbox credentials to
          enable card payments.
        </div>
      );
    }

    const postalCodeInvalid = postalCode.length > 0 && !POSTAL_CODE_RE.test(postalCode.trim());

    return (
      <div className="square-payment-form">
        <h3 className="square-payment-title">Payment</h3>
        {config.environment === 'sandbox' && (
          <p className="square-payment-hint">
            Sandbox mode — use Square test card{' '}
            <code>4111 1111 1111 1111</code>, any future expiry, any CVV.
          </p>
        )}
        {loading && <p className="square-payment-loading">Loading secure payment form...</p>}
        {error && <div className="status-banner error">{error}</div>}
        <div
          id="square-card-container"
          ref={containerRef}
          className={`square-card-container${disabled ? ' is-disabled' : ''}`}
        />
        <div className="postal-code-field">
          <label htmlFor="billing-postal-code" className="postal-code-label">
            Postal Code
          </label>
          <input
            id="billing-postal-code"
            type="text"
            inputMode="text"
            autoComplete="postal-code"
            placeholder="V8V 1A1"
            maxLength={7}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            disabled={disabled}
            className={`postal-code-input${postalCodeInvalid ? ' postal-code-invalid' : ''}`}
          />
          {postalCodeInvalid && (
            <span className="postal-code-error">Enter a valid Canadian postal code</span>
          )}
        </div>
      </div>
    );
  }
);

export default SquarePaymentForm;
